import { useEffect, useState } from 'react';
import { Handshake } from 'lucide-react';
import type { Deal, TakeoverResult } from '@epasu/core';
import { ZIELGRUPPEN_OHNE_PROJEKT } from '@epasu/core/constants';
import { crm, type ContactRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';

/**
 * Die Übernahme: aus der Firma auf der Zusage-Spalte wird ein Kunde, auf
 * Wunsch mit dem ersten Projekt aus einer Vorlage.
 *
 * Das ist der einzige Übergang von der Akquise in die Projektarbeit — ein
 * Klick, eine Transaktion (`customers.takeOver`). Der Dialog entscheidet
 * nichts selbst, er sammelt nur: welcher Ansprechpartner, ab wann, welche
 * Vorlage.
 *
 * **Fotografen bekommen kein Projekt.** Für sie gibt es keine Projektarbeit,
 * nur das Partnernetzwerk; die Auswahl steht fest auf „Kein Projekt“ und ist
 * nicht anwählbar. Schulen, Universitäten und Unternehmen wählen selbst: die
 * Vorlage ist **nicht** vorbelegt; der Dialog verlangt eine ausdrückliche Wahl —
 * eine Vorlage oder „Kein Projekt“ —, und der Knopf bleibt aus, bis sie
 * getroffen ist. Vorlagen haben keine Zielgruppe; eine vorbelegte wäre eine
 * willkürliche Wahl, und wer durchklickt, legte still ein falsches Projekt an
 * einem echten Kunden an. Ein Klick mehr ist billiger als das Aufräumen.
 */

const KEIN_PROJEKT = '';

/**
 * Das heutige Datum der Ortszeit als `JJJJ-MM-TT`. `toISOString()` rechnet in
 * UTC und lieferte zwischen 0 und 2 Uhr deutscher Zeit den gestrigen Tag — und
 * der steht danach als „Kunde seit“ im Kundendatensatz.
 */
function heute(): string {
  const jetzt = new Date();
  const zweistellig = (n: number) => String(n).padStart(2, '0');
  return `${jetzt.getFullYear()}-${zweistellig(jetzt.getMonth() + 1)}-${zweistellig(jetzt.getDate())}`;
}
/** Noch nichts gewählt — weder Vorlage noch „Kein Projekt“. */
const UNGEWAEHLT = '__ungewaehlt__';

interface Props {
  deal: Deal;
  /** `audiences.key` der Pipeline, in der die Karte steht */
  audienceKey: string;
  onClose: () => void;
  onDone: (ergebnis: TakeoverResult & { organizationId: string; organizationName: string }) => void;
}

export function TakeoverDialog({ deal, audienceKey, onClose, onDone }: Props) {
  const keinProjektMoeglich = ZIELGRUPPEN_OHNE_PROJEKT.includes(audienceKey);

  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState('');
  const [firmaFehlt, setFirmaFehlt] = useState(false);
  const [kontakte, setKontakte] = useState<ContactRow[]>([]);
  const [hauptkontakt, setHauptkontakt] = useState('');
  const [seit, setSeit] = useState(heute());
  const [vorlagen, setVorlagen] = useState<{ id: string; name: string }[]>([]);
  const [vorlageId, setVorlageId] = useState<string>(UNGEWAEHLT);
  const [projektTitel, setProjektTitel] = useState('');
  const [laedt, setLaedt] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // Firma, Ansprechpartner und Vorlagen laden. Die Firma steht am Deal; fehlt
  // sie dort, hilft die des Kontakts — dieselbe Rückfalllinie wie bei den
  // verlorenen Deals. Fehlt auch die, bleibt der Dialog stehen und sagt, was
  // fehlt: ein Kunde ist immer eine Firma.
  useEffect(() => {
    let abgebrochen = false;
    void (async () => {
      try {
        const [liste, kontakt] = await Promise.all([
          crm.projectTemplates.list(),
          deal.contactId ? crm.contacts.get(deal.contactId) : Promise.resolve(null),
        ]);
        if (abgebrochen) return;
        setVorlagen(liste.map((v) => ({ id: v.id, name: v.name })));

        const firmaId = deal.organizationId ?? kontakt?.organizationId ?? null;
        if (!firmaId) {
          setFirmaFehlt(true);
          return;
        }
        const [firma, mitarbeiter] = await Promise.all([
          crm.organizations.get(firmaId),
          crm.contacts.list({ organizationId: firmaId }),
        ]);
        if (abgebrochen) return;
        setOrganizationId(firmaId);
        setOrganizationName(firma?.name ?? deal.title);
        setKontakte(mitarbeiter);
        // Der Kontakt am Deal ist der Ansprechpartner, mit dem die Zusage kam.
        if (deal.contactId && mitarbeiter.some((k) => k.id === deal.contactId)) {
          setHauptkontakt(deal.contactId);
        }
      } catch (err) {
        if (!abgebrochen) {
          setFehler(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
        }
      } finally {
        if (!abgebrochen) setLaedt(false);
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [deal.contactId, deal.organizationId, deal.title, keinProjektMoeglich]);

  const uebernehmen = async () => {
    if (!organizationId) return;
    setBusy(true);
    setFehler(null);
    try {
      // `null` heißt ausdrücklich „kein Projekt“ — nicht „Vorlage vergessen“.
      const waehlbar = !keinProjektMoeglich && vorlageId !== KEIN_PROJEKT && vorlageId !== UNGEWAEHLT;
      const ergebnis = await crm.customers.takeOver({
        organizationId,
        primaryContactId: hauptkontakt || null,
        projectTemplateId: waehlbar ? vorlageId : null,
        projectTitle: waehlbar ? projektTitel.trim() || null : null,
        since: seit || null,
      });
      onDone({ ...ergebnis, organizationId, organizationName });
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Übernehmen fehlgeschlagen');
      setBusy(false);
    }
  };

  const mitProjekt =
    !keinProjektMoeglich && vorlageId !== KEIN_PROJEKT && vorlageId !== UNGEWAEHLT;
  // Fotografen haben nichts zu wählen; alle anderen müssen sich entscheiden.
  const entschieden = keinProjektMoeglich || vorlageId !== UNGEWAEHLT;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6">
      <div className="w-full max-w-lg space-y-4 rounded-lg bg-card p-6 shadow-xl">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Handshake className="h-5 w-5 text-muted-foreground" />
          Als Kunde übernehmen
        </h2>

        {laedt ? (
          <p className="text-sm text-muted-foreground">Lade …</p>
        ) : firmaFehlt ? (
          <p className="rounded-md bg-secondary p-3 text-sm text-muted-foreground">
            Am Deal „{deal.title}“ hängt keine Firma, und sein Kontakt gehört zu keiner.
            Ein Kunde ist immer eine Firma — ordne dem Deal über „Bearbeiten“ einen
            Kontakt mit Firma zu, dann lässt er sich übernehmen.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{organizationName}</span> wird
              Kunde. Die Karte verlässt damit das Brett; der Deal bleibt als Herkunft
              erhalten.
            </p>

            <label className="block space-y-1 text-sm">
              <span className="font-medium">Hauptansprechpartner</span>
              <Select
                value={hauptkontakt}
                onChange={(e) => setHauptkontakt(e.target.value)}
                disabled={kontakte.length === 0}
              >
                <option value="">— keiner —</option>
                {kontakte.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.displayName}
                  </option>
                ))}
              </Select>
            </label>

            <label className="block space-y-1 text-sm">
              <span className="font-medium">Kunde seit</span>
              <Input type="date" value={seit} onChange={(e) => setSeit(e.target.value)} />
            </label>

            <label className="block space-y-1 text-sm">
              <span className="font-medium">Erstes Projekt aus Vorlage</span>
              <Select
                value={keinProjektMoeglich ? KEIN_PROJEKT : vorlageId}
                required
                onChange={(e) => setVorlageId(e.target.value)}
                disabled={keinProjektMoeglich}
                aria-label="Projektvorlage"
              >
                {!keinProjektMoeglich && (
                  <option value={UNGEWAEHLT} disabled>
                    — bitte wählen —
                  </option>
                )}
                <option value={KEIN_PROJEKT}>Kein Projekt</option>
                {!keinProjektMoeglich &&
                  vorlagen.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
              </Select>
              {keinProjektMoeglich && (
                <span className="block text-xs text-muted-foreground">
                  Für Fotografen gibt es kein Projekt — sie gehören ins Partnernetzwerk.
                </span>
              )}
              {!keinProjektMoeglich && vorlagen.length === 0 && (
                <span className="block text-xs text-muted-foreground">
                  Noch keine Projektvorlage angelegt — die Übernahme legt dann kein
                  Projekt an.
                </span>
              )}
            </label>

            {mitProjekt && (
              <label className="block space-y-1 text-sm">
                <span className="font-medium">Projekttitel (optional)</span>
                <Input
                  value={projektTitel}
                  onChange={(e) => setProjektTitel(e.target.value)}
                  placeholder="Ohne Titel trägt das Projekt den Namen der Vorlage"
                />
              </label>
            )}
          </>
        )}

        {fehler && <p className="text-sm text-danger">{fehler}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          {organizationId && (
            <Button onClick={() => void uebernehmen()} disabled={busy || laedt || !entschieden}>
              {busy ? 'Übernehme …' : 'Als Kunde übernehmen'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
